import * as Yup from "yup";
import React, { Fragment, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import {
  Alert,
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../snackbar";
import Iconify from "../../iconify";
import { useBoolean } from "../../../hooks/use-boolean";
import { getValue } from "@mui/system";
import { TramRounded } from "@mui/icons-material";

export default function UserPassIntegration() {
  const SERVER_URI = process.env.REACT_APP_SERVER_URI;
  const AUTHENTIK_LOGIN_URL = `${SERVER_URI}/ssologin`;
  const SIGN_UP_URL = `${SERVER_URI}/freeUserSignupLdap`;
  const CHECK_USERNAME = `${SERVER_URI}/checkusername`;

  const passwordShow = useBoolean();

  const { enqueueSnackbar } = useSnackbar();
  const [emailData, setEmailData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [userAvailable, setUserAvailable] = useState(false);
  const [userMsg, setUserMsg] = useState(false);

  const navigate = useNavigate(); // for redirection

  // Check if 'temptoken' exists, if not redirect to the signup page
  React.useEffect(() => {
    const tempToken = sessionStorage.getItem("temptoken");
    if (!tempToken) {
      setTimeout(() => {
        navigate("/user_signup");
      }, 100);
  
    }
  }, [navigate]);

  const UpdateUserPassSchema = Yup.object().shape({
    name: Yup.string()
      .min(1, "First Name must be at least 5 characters")
      .max(50, "First Name must not exceed 50 characters")
      .matches(
        /^[a-zA-Z]+$/,
        "Number, spaces or special characters are not allowed"
      )
      .required("First Name is required"),

    lastName: Yup.string()
      .min(1, "Last Name must be at least 5 characters")
      .max(50, "Last Name must not exceed 50 characters")
      .matches(
        /^[a-zA-Z]+$/,
        "Number, spaces or special characters are not allowed"
      )
      .required("Last Name is required"),
    username: Yup.string()
      .min(5, "User Name must be at least 5 characters")
      .max(50, "User Name must not exceed 50 characters")
      .matches(/^[a-zA-Z0-9]+$/, "Spaces or special characters are not allowed")
      .required("User Name is required"),
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Email must be a valid email address"
      )
      .test(
        "is-allowed-domain",
        "Email domain must be one of the following: gmail.com, yahoo.com, outlook.com, protonmail.com, hotmail.com, zoho.com",
        (value) => {
          if (!value) return false;
          const domain = value.split("@")[1];
          return ["gmail.com", "yahoo.com", "outlook.com", "protonmail.com","mail.com","zoho.com","megaconnect.cloud"].includes(domain);
        }
      ),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
    // .oneOf([Yup.ref('confirmPassword')], 'Passwords must match'),

    confirmPassword: Yup.string()
      .min(8, "Confirm Password must be at least 8 characters")
      .required("Confirm password is required")
      .oneOf([Yup.ref("password")], "Passwords must match"),

    term_conditions: Yup.bool()
      .oneOf([true], "You must accept the terms and conditions")
      .required("You must accept the terms and conditions"),
  });
  const storedFormData = JSON.parse(sessionStorage.getItem("formData")) || {};
  // const defaultValues = {
  //   name: emailData?.name || "",
  //   lastName: emailData?.lastName || "",
  //   username: emailData?.username || "",
  //   password: emailData?.password || "",
  //   term_conditions: emailData?.term_conditions || false,
  // };
  const defaultValues = {
    name: storedFormData.name || "",
    lastName: storedFormData.lastName || "",
    username: storedFormData.username || "",
    email: storedFormData.email || "",
    password: "", // exclude saved values for password
    confirmPassword: "", // exclude saved values for confirm password
    term_conditions: storedFormData.term_conditions || false,
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
    trigger,
    reset,
  } = useForm({
    resolver: yupResolver(UpdateUserPassSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  React.useEffect(() => {
    const formData = {
      name: getValues("name"),
      lastName: getValues("lastName"),
      username: getValues("username"),
      email: getValues("email"),
      term_conditions: getValues("term_conditions"),
    };
    sessionStorage.setItem("formData", JSON.stringify(formData));
  }, [
    getValues,
    getValues("name"),
    getValues("lastName"),
    getValues("username"),
    getValues("email"),
    getValues("term_conditions"),
  ]);

  const checkUsernameAvailability = async () => {
    setUserAvailable(false);
    const isUserNameValid = await trigger("username");
    // Trigger validation for the email field
    if (!isUserNameValid) {
    
      setUserMsg("Please enter a valid username");
      return;
    }

    const username = getValues("username");

    try {
      const response = await axios.post(CHECK_USERNAME, { username });
     if(response.data.message === "Username is available") {
        setUserAvailable(true);
        console.log("Username is available")
      }else{
        setUserAvailable(false);
        console.log("Always false")
      }
      setUserMsg(response.data.message);
    } catch (error) {
      console.error("Error Message:", error.response.data.message);
      if(error.response.data.message === "Username already exists"){
        setUserAvailable(false);
        console.log("Username already exists")
      }
      // setUserMsg(error.response.data.message);
      return false;
    }
  };

  const [userName, setUserName] = React.useState("");
  React.useEffect(() => {
    if (userName) {
      const delayDebounceFn = setTimeout(() => {
        checkUsernameAvailability(userName); // Call the API after a delay (debounce)
      }, 2000);

      return () => clearTimeout(delayDebounceFn); // Cleanup
    }
  }, [userName]);

  const onSubmit = async (data) => {
    setLoading(true);
    const isNameValid = await trigger("name");
    const isLastNameValid = await trigger("lastName");

    const isUserNameValid = await trigger("username");
    const isEmailValid = await trigger("email");
    const isPasswordValid = await trigger("password");
    const isTermsAccepted = getValues("term_conditions");

    if (!isNameValid) {
      setErrorMessage("Please enter a valid Name.");
      return;
    }
    if (!isLastNameValid) {
      setErrorMessage("Please enter a valid LastName.");
      return;
    }
    // Trigger validation for the email field
    if (!isUserNameValid) {
      setUserAvailable(false);
      setErrorMessage("");
      return;
    }
    if (!isEmailValid) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!isPasswordValid) {
      setErrorMessage("Please enter a valid Password.");
      return;
    }
    if (!isTermsAccepted) {
      setErrorMessage("You must accept the terms and conditions.");
      setLoading(false);
      return;
    }
    const name = getValues("name");
    const lastName = getValues("lastName");
    const username = getValues("username");
    const email = getValues("email");
    const password = getValues("password");
    // const term_conditions=getValue("terms_conditions")
    let token = sessionStorage.getItem("temptoken");
    let config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    try {
      const response = await axios.post(
        SIGN_UP_URL,
        {
          name,
          lastName,
          username,
          email,
          password,
        },
        config
      );
      setErrorMessage("");
      enqueueSnackbar(response.data.message, { variant: "success" });
      reset();
      console.log("AccessToken",response)
      localStorage.setItem("accessToken", response.data.accessToken);
      setValue("confirmPassword", "");
      setUserMsg("");
      sessionStorage.removeItem("storedFormData");
      setTimeout(() => {
        // Check if the plan is selected or not
        const storedPlan = sessionStorage.getItem("selectedPlan");

        if (!storedPlan) {
          // If no plan is selected, navigate to the plans page
          window.location.href = "/plans";
        } else {
          // If a plan is selected, navigate to payment proceed page
          window.location.href = "/paymenthome";
        }
      }, 3000);
    } catch (error) {
      console.log(error.response.data.message)
      if (!navigator.onLine) {
        setErrorMessage("You are currently offline. Please check your network connection.");
      } else if(error.response && error.response.status === 409){
        setErrorMessage(error.response.data.warning);
      }else if (error.response && error.response.status === 400) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage(error.message || "An error occurred while processing your request.");
      }
    }finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={2} sx={{ mt: 5, mb: 5 }}>
          <Box>
            <Stack direction="column">
              <Typography
                variant="h3"
                sx={{
                  color: "#1D53E7",
                  fontFamily: "Sofia Sans Extra Condensed",
                  // fontStyle: "italic",
                  fontWeight: 700,
                }}
              >
                SIGN UP NOW{" "}
              </Typography>
              {/* <Typography
                                sx={{
                                    fontSize: 18,
                                    fontFamily: "Roboto Flex",
                                    fontWeight: "400",
                                    wordWrap: "break-word",
                                }}
                            >
                                Where Your Digital Universe Unfolds!
                            </Typography> */}
            </Stack>
          </Box>
          <Box>
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={2}
              sx={{ width: "100%" }}
            >
              <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                <Typography
                  component="label"
                  htmlFor="name_text"
                  sx={{
                    fontWeight: 400,
                    fontFamily: "Roboto Flex",
                    fontSize: "14px",
                    color: "#1F1F1F",
                  }}
                >
                  First Name <span style={{ color: "red" }}> *</span>
                </Typography>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="name_text"
                      variant="outlined"
                      size="medium"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  )}
                />
              </Stack>
              <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                <Typography
                  component="label"
                  htmlFor="lastName_text"
                  sx={{
                    fontWeight: 400,
                    fontFamily: "Roboto Flex",
                    fontSize: "14px",
                    color: "#1F1F1F",
                  }}
                >
                  Last Name <span style={{ color: "red" }}> *</span>
                </Typography>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="lastName_text"
                      variant="outlined"
                      size="medium"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Box>
          <Box>
          <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={2}
              sx={{ width: "100%" }}
            >
            <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
              <Typography
                component="label"
                htmlFor="username_text"
                sx={{
                  fontWeight: 400,
                  fontFamily: "Roboto Flex",
                  fontSize: "14px",
                  color: "#1F1F1F",
                }}
              >
                Username <span style={{ color: "red" }}> *</span>
              </Typography>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="username_text"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.username}
                    // helperText={errors.username?.message}
                    helperText={
                      errors.username
                        ? errors.username?.message // Show when phone is invalid
                        : userAvailable
                          ? userMsg
                          : "-"
                            ? userMsg
                            : "-"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                      },
                      marginTop: "5px",
                      "& .MuiFormHelperText-root": {
                        color:
                          !errors.username && userAvailable ? "green" : "red", // Green for success (OTP sent), red for error (invalid phone)
                      },
                    }}
                    onChange={(e) => {
                      field.onChange(e); // Call field's onChange to update form state
                      setUserName(e.target.value); // Update userName state
                    }}
                  />
                )}
              />
             
            </Stack>
            <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                <Typography
                  component="label"
                  htmlFor="lastName_text"
                  sx={{
                    fontWeight: 400,
                    fontFamily: "Roboto Flex",
                    fontSize: "14px",
                    color: "#1F1F1F",
                  }}
                >
                  Email Address <span style={{ color: "red" }}> *</span>
                </Typography>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="email_text"
                      variant="outlined"
                      size="medium"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />
                  )}
                />
              </Stack>
              </Stack>
          </Box>

          <Box>
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={2}
              mb={2}
              sx={{ width: "100%" }}
            >
              <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                <Typography
                  component="label"
                  htmlFor="password_text"
                  sx={{
                    fontWeight: 400,
                    fontFamily: "Roboto Flex",
                    fontSize: "14px",
                    color: "#1F1F1F",
                  }}
                >
                  Password <span style={{ color: "red" }}> *</span>
                </Typography>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="password_text"
                      variant="outlined"
                      size="medium"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                      type={passwordShow.value ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={passwordShow.onToggle}>
                              <Iconify
                                icon={
                                  passwordShow.value
                                    ? "solar:eye-bold"
                                    : "solar:eye-closed-bold"
                                }
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
              <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                <Typography
                  component="label"
                  htmlFor="confirmPassword_text"
                  sx={{
                    fontWeight: 400,
                    fontFamily: "Roboto Flex",
                    fontSize: "14px",
                    color: "#1F1F1F",
                  }}
                >
                  Confirm Password <span style={{ color: "red" }}> *</span>
                </Typography>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="confirmPassword_text"
                      variant="outlined"
                      size="medium"
                      fullWidth
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                      type={passwordShow.value ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={passwordShow.onToggle}>
                              <Iconify
                                icon={
                                  passwordShow.value
                                    ? "solar:eye-bold"
                                    : "solar:eye-closed-bold"
                                }
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
            </Stack>

            {errorMessage && (
              <Alert
                variant="outlined"
                severity="error"
                sx={{ mt: 1, borderRadius: 3 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setErrorMessage(null);
                    }}
                  >
                    <Iconify color="red" icon="basil:cross-outline" />
                  </IconButton>
                }
              >
                {errorMessage}
              </Alert>
            )}
            {errors.term_conditions && (
              <Alert
                variant="outlined"
                severity="error"
                sx={{ mt: 1, borderRadius: 3 }}
              >
                {errors.term_conditions?.message}
              </Alert>
            )}
            {/* {Object.keys(errors).length > 0 && (
              <Alert
                variant="outlined"
                severity="error"
                sx={{ mt: 1, borderRadius: 3 }}
              >
                {Object.values(errors).map((error, index) => (
                  <div key={index}>{error.message}</div>
                ))}
              </Alert>
            )} */}
          </Box>
          <Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                textAlign: "center",
                alignItems: "center",
                // justifyContent: 'center'
              }}
            >
              <Controller
                name="term_conditions"
                control={control}
                render={({ field }) => <Checkbox {...field}
                error={!!errors.term_conditions}
          sx={{
            color: errors.term_conditions ? "red" : undefined, // Optional: Change checkbox color on error
          }}
           />}
              />

              <Typography
                component="div"
                sx={{
                  // display: "flex",
                  fontSize: 16,
                  fontFamily: "Roboto Flex",
                  fontWeight: "400",
                  wordWrap: "break-word",
                  textAlign: "justify",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                By creating an account, I agree to our &nbsp;
                <Link
                  to="/terms&condition"
                  style={{ color: "#1E53E8" }}
                  // onClick={() =>
                  //     window.open(AUTHENTIK_LOGIN_URL, "_self")
                  // }
                >
                  Terms of use
                </Link>
                &nbsp; and &nbsp;
                <Link
                  to="/privacy"
                  style={{ color: "#1E53E8" }}
                  // onClick={() =>
                  //     window.open(AUTHENTIK_LOGIN_URL, "_self")
                  // }
                >
                  Privacy Policy
                </Link>
              </Typography>
            </Stack>
          </Box>
          <Box>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              sx={{
                textAlign: "center",
                alignItems: "center",
                // justifyContent:'center'
              }}
            >
              <LoadingButton
                size="large"
                sx={{
                  fontFamily: "Roboto Flex",
                  // width: "150px",
                  backgroundColor: "#C7F760",
                  color: "#1E53E8",
                  textTransform: "none",
                  fontWeight: 900,
                  "&:hover": {
                    backgroundColor: "#1E53E8",
                    color: "#C7F760",
                  },
                }}
                type="submit"
                loading={loading}
                variant="contained"
              >
                Create Account
              </LoadingButton>
              <Typography
                sx={{
                  fontSize: 16,
                  fontFamily: "Roboto Flex",
                  fontWeight: "400",
                  wordWrap: "break-word",
                  textAlign: "center",
                  alignItems: "center",
                }}
              >
                Already have an account?{" "}
                <Link
                  to="/user_Login"
                  style={{ color: "#1E53E8" }}
                  // onClick={() => window.open(AUTHENTIK_LOGIN_URL, "_self")}
                >
                  Log in
                </Link>
              </Typography>
            </Stack>
            
          </Box>
        </Stack>
      </form>
    </Fragment>
  );
}
